import React, { useState } from "react";
import axios from "axios";
import { adminAuthHeader } from "@/hooks/useAdminAuth";
import { Send, ChevronDown, ChevronUp, CheckCircle2, XCircle, Loader2, Copy, Check } from "lucide-react";
import { EVENT_LABELS } from "@/utils/eventLabels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Ordered list of events you'll typically map in Make.com.
const EVENTS_TO_TEST = [
  { name: "lead_generated", emoji: "🧲" },
  { name: "lead_completato", emoji: "✅" },
  { name: "lead_recupero_password_inviato", emoji: "📤" },
  { name: "lesson_1_started", emoji: "▶️" },
  { name: "tempo_pagina_50pct_lezione_1", emoji: "📈" },
  { name: "tempo_pagina_80pct_lezione_1", emoji: "📈" },
  { name: "lesson_1_completed", emoji: "🏁" },
  { name: "lesson_2_started", emoji: "▶️" },
  { name: "tempo_pagina_50pct_lezione_2", emoji: "📈" },
  { name: "tempo_pagina_80pct_lezione_2", emoji: "📈" },
  { name: "lesson_2_completed", emoji: "🏁" },
  { name: "lesson_3_started", emoji: "▶️" },
  { name: "tempo_pagina_50pct_lezione_3", emoji: "📈" },
  { name: "tempo_pagina_80pct_lezione_3", emoji: "📈" },
  { name: "lesson_3_completed", emoji: "🏁" },
  { name: "purchase_lesson_2_success", emoji: "💶" },
  { name: "purchase_lesson_3_success", emoji: "💶" },
  { name: "purchase_full_course_success", emoji: "💶" },
  { name: "popup_consulenza_gratuita_submitted", emoji: "🎁" },
  { name: "popup_consultation_submitted", emoji: "💰" },
  { name: "contact_request_full_course", emoji: "🔥" },
];

export default function TestWebhookPanel() {
  const [open, setOpen] = useState(false);
  // state per row: { loading, status: "ok"|"error"|null, payload, error }
  const [rowStates, setRowStates] = useState({});
  const [copied, setCopied] = useState("");

  const fireEvent = async (eventName) => {
    setRowStates((s) => ({ ...s, [eventName]: { loading: true } }));
    try {
      const { data } = await axios.post(
        `${API}/admin/test-webhook`,
        { event_name: eventName },
        { headers: adminAuthHeader() },
      );
      setRowStates((s) => ({
        ...s,
        [eventName]: {
          loading: false,
          status: data.make_status === "ok" ? "ok" : "warn",
          make_status: data.make_status,
          payload: data.payload_sent,
        },
      }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setRowStates((s) => ({ ...s, [eventName]: { loading: false, status: "error", error: detail } }));
    }
  };

  const copyJson = (eventName, payload) => {
    try {
      navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(eventName);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* ignore */ }
  };

  return (
    <section data-testid="test-webhook-panel" className="bg-white rounded-2xl border border-[#dbe3ec] shadow-[0_2px_30px_-15px_rgba(26,42,58,0.18)] overflow-hidden mb-7">
      <button
        type="button"
        data-testid="test-webhook-toggle"
        onClick={() => setOpen(!open)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-[#f7fafd] transition"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">🧪</span>
          <div>
            <p className="font-semibold" style={{ color: "var(--np-primary)" }}>Test Webhook Make.com</p>
            <p className="text-[12px]" style={{ color: "var(--np-muted)" }}>
              Manda eventi di esempio a Make.com per mappare i campi (&quot;Redetermine Data Structure&quot;)
            </p>
          </div>
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {open && (
        <div className="border-t border-[#eef2f7] px-6 py-5 bg-[#fafcfe]">
          <div className="text-[13px] mb-4 leading-relaxed rounded-md bg-white border border-[#dbe3ec] px-3 py-2.5" style={{ color: "var(--np-muted)" }}>
            <p className="font-semibold mb-1" style={{ color: "var(--np-primary)" }}>Come si usa</p>
            <ol className="list-decimal pl-5 space-y-0.5">
              <li>Apri il tuo scenario Make.com → clicca sul modulo Webhook → <strong>Redetermine Data Structure</strong></li>
              <li>Clicca <strong>Invia test</strong> qui sotto sull&apos;evento da mappare</li>
              <li>Make.com cattura la struttura → premi <em>Save</em> e mappa i campi</li>
              <li>Il payload include <code>_test: true</code> — filtralo in produzione su Make.com se vuoi.</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {EVENTS_TO_TEST.map(({ name, emoji }) => {
              const st = rowStates[name] || {};
              const label = EVENT_LABELS[name]?.label || name;
              return (
                <div key={name} data-testid={`test-event-${name}`}
                  className="bg-white border border-[#dbe3ec] rounded-lg px-3.5 py-3 flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium truncate" style={{ color: "var(--np-primary)" }}>
                        <span className="mr-1.5">{emoji}</span>{label}
                      </p>
                      <p className="text-[10.5px] font-mono truncate" style={{ color: "var(--np-muted)" }}>{name}</p>
                    </div>
                    <button
                      type="button"
                      data-testid={`fire-event-${name}`}
                      onClick={() => fireEvent(name)}
                      disabled={st.loading}
                      className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-full border border-[#dbe3ec] bg-white hover:border-[var(--np-secondary)] hover:text-[var(--np-secondary)] disabled:opacity-60"
                      style={{ color: "var(--np-primary)" }}
                    >
                      {st.loading
                        ? <><Loader2 className="animate-spin" size={12} /> Invio…</>
                        : st.status === "ok"
                          ? <><CheckCircle2 size={12} className="text-emerald-600" /> Re-invia</>
                          : st.status === "error"
                            ? <><XCircle size={12} className="text-red-600" /> Riprova</>
                            : <><Send size={12} /> Invia test</>}
                    </button>
                  </div>

                  {st.status === "ok" && st.payload && (
                    <div className="rounded-md bg-[#f5f8fc] border border-[#eef2f7] p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "var(--np-muted)" }}>
                          Make.com: {st.make_status}
                        </span>
                        <button
                          type="button"
                          data-testid={`copy-payload-${name}`}
                          onClick={() => copyJson(name, st.payload)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold hover:text-[var(--np-secondary)]"
                          style={{ color: "var(--np-muted)" }}
                        >
                          {copied === name ? <><Check size={11} /> Copiato</> : <><Copy size={11} /> Copia JSON</>}
                        </button>
                      </div>
                      <pre className="text-[10px] leading-snug overflow-x-auto max-h-32 font-mono whitespace-pre-wrap break-all"
                        style={{ color: "var(--np-primary)" }}>
                        {JSON.stringify(st.payload, null, 2)}
                      </pre>
                    </div>
                  )}

                  {st.status === "warn" && st.payload && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      Webhook salvato in DB ma Make.com risponde: <code>{st.make_status}</code>
                    </p>
                  )}

                  {st.status === "error" && (
                    <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                      {st.error}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
